import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import UnifiedSuperAppObjectEditor from "../components/UnifiedSuperAppObjectEditor";
import SuperAppButton from "../components/SuperAppButton";
import { useSuperAppConfig } from "../lib/superappConfig";

/**
 * EDIT MODE INTERFACE
 * 
 * Allows editing pages by:
 * 1. Selecting a page (click "home" etc to edit)
 * 2. Choosing a layout (4 options: single, 2-col, 3-col, 4-col/grid)
 * 3. Adding blocks via quadrant/grid view
 * 4. Creating/editing SuperAppObjects
 * 
 * Access: /edit-mode?appId=Ben-test
 */

// Helper to always hit same-origin API
const api = (path, options = {}) =>
  fetch(`/api${path}`, {
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "same-origin",
    ...options,
  });

const LAYOUT_OPTIONS = [
  { value: "single", label: "Single Column", structure: { type: "row", children: [{ type: "column", children: [] }] } },
  { value: "two", label: "Two Columns", structure: { type: "row", children: [{ type: "column", children: [] }, { type: "column", children: [] }] } },
  { value: "three", label: "Three Columns", structure: { type: "row", children: [{ type: "column", children: [] }, { type: "column", children: [] }, { type: "column", children: [] }] } },
  { value: "four", label: "Four Columns / Grid", structure: { type: "row", children: [{ type: "column", children: [] }, { type: "column", children: [] }, { type: "column", children: [] }, { type: "column", children: [] }] } },
];

const BLOCK_TYPES = [
  { type: "text", label: "Text Block", icon: "📝", defaultData: { type: "text", title: "New Text Block", value: "Enter your text here" } },
  { type: "html", label: "HTML Block", icon: "🔧", defaultData: { type: "html", title: "New HTML Block", html: "<p>Enter your HTML here</p>" } },
  { type: "markdown", label: "Markdown Block", icon: "📄", defaultData: { type: "markdown", title: "New Markdown Block", text: "Enter your markdown here" } },
  { type: "superappobject", label: "SuperAppObject", icon: "📊", defaultData: { type: "superappobject", configObj: "", displayState: "TABLE" } },
];

export default function EditMode() {
  const location = useLocation();
  const history = useHistory();
  const appId = new URLSearchParams(location.search).get("appId") || "Ben-test";
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [selectedPage, setSelectedPage] = useState("home");
  const [selectedLayout, setSelectedLayout] = useState("single");
  const [blocks, setBlocks] = useState({});
  const [layoutStructure, setLayoutStructure] = useState(null);
  const [showSuperAppObjectEditor, setShowSuperAppObjectEditor] = useState(false);
  const [configObjList, setConfigObjList] = useState([]);
  
  // Load config on mount
  useEffect(() => {
    loadConfig();
  }, [appId]);
  
  // Load configObj list for SuperAppObject blocks
  useEffect(() => {
    loadConfigObjList();
  }, []);
  
  // Update layout structure when selectedLayout changes
  useEffect(() => {
    if (selectedLayout && cfg?.screens?.[selectedPage]) {
      const layoutOption = LAYOUT_OPTIONS.find(opt => opt.value === selectedLayout);
      if (layoutOption) {
        const currentLayout = cfg.screens[selectedPage].layout;
        const newStructure = JSON.parse(JSON.stringify(layoutOption.structure));
        
        // Preserve existing blocks if they exist
        if (currentLayout?.blocks) {
          setBlocks(currentLayout.blocks);
        }
        
        // If there are existing blocks, try to distribute them into the new structure
        if (currentLayout?.structure && currentLayout.blocks) {
          const existingBlocks = Object.keys(currentLayout.blocks);
          if (existingBlocks.length > 0) {
            // Distribute blocks across columns
            const columns = newStructure.children || [];
            existingBlocks.forEach((blockKey, idx) => {
              const colIdx = idx % columns.length;
              if (!columns[colIdx].children) columns[colIdx].children = [];
              columns[colIdx].children.push({ type: "block", block: blockKey });
            });
          }
        }
        
        setLayoutStructure(newStructure);
      }
    }
  }, [selectedLayout, selectedPage, cfg]);
  
  /**
   * Load SuperApp config from Firestore
   */
  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await api(`/superapp/config/${encodeURIComponent(appId)}`);
      if (!response.ok) {
        throw new Error(`Failed to load config: ${response.status}`);
      }
      const data = await response.json();
      const config = data.config || data;
      setCfg(config);
      
      // Initialize selected page
      if (config.screens && Object.keys(config.screens).length > 0) {
        const firstPage = Object.keys(config.screens)[0];
        setSelectedPage(firstPage);
        const screen = config.screens[firstPage];
        if (screen.layout) {
          setBlocks(screen.layout.blocks || {});
          setLayoutStructure(screen.layout.structure || null);
          
          // Determine layout type from structure
          if (screen.layout.structure) {
            const cols = screen.layout.structure.children || [];
            if (cols.length === 1) setSelectedLayout("single");
            else if (cols.length === 2) setSelectedLayout("two");
            else if (cols.length === 3) setSelectedLayout("three");
            else if (cols.length === 4) setSelectedLayout("four");
            else setSelectedLayout("single");
          }
        }
      }
    } catch (e) {
      console.error("Failed to load config:", e);
      alert(`Error loading config: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Load list of available configObjs
   */
  const loadConfigObjList = async () => {
    try {
      const response = await api("/getObjects/config");
      const configArray = await response.json();
      if (Array.isArray(configArray) && configArray.length > 0) {
        const fullConfig = configArray[0];
        const configObjNames = fullConfig.app_config ? Object.keys(fullConfig.app_config) : [];
        setConfigObjList(configObjNames);
      }
    } catch (e) {
      console.error("Failed to load configObj list:", e);
    }
  };
  
  /**
   * Save config back to Firestore
   */
  const saveConfig = async () => {
    if (!cfg || !selectedPage) return;
    
    try {
      setSaving(true);
      
      // Update the selected page's layout
      const updatedCfg = JSON.parse(JSON.stringify(cfg));
      if (!updatedCfg.screens) updatedCfg.screens = {};
      if (!updatedCfg.screens[selectedPage]) updatedCfg.screens[selectedPage] = {};
      
      updatedCfg.screens[selectedPage].layout = {
        structure: layoutStructure,
        blocks: blocks,
      };
      
      // Save to API
      const response = await api(`/superapp/config/${encodeURIComponent(appId)}`, {
        method: "PUT",
        body: JSON.stringify(updatedCfg),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to save: ${response.status}`);
      }
      
      setCfg(updatedCfg);
      alert("Page saved successfully!");
    } catch (e) {
      console.error("Failed to save config:", e);
      alert(`Error saving: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };
  
  /**
   * Add a new block to the page
   */
  const addBlock = (blockType) => {
    const blockTypeDef = BLOCK_TYPES.find(bt => bt.type === blockType);
    if (!blockTypeDef) return;
    
    const blockKey = `${blockType}_${Date.now()}`;
    const newBlock = JSON.parse(JSON.stringify(blockTypeDef.defaultData));
    
    // For SuperAppObject, show editor first
    if (blockType === "superappobject") {
      setShowSuperAppObjectEditor(true);
      // We'll handle the actual block creation after SuperAppObject is configured
      return;
    }
    
    const newBlocks = { ...blocks, [blockKey]: newBlock };
    setBlocks(newBlocks);
    
    // Add block to first available column
    if (layoutStructure && layoutStructure.children && layoutStructure.children.length > 0) {
      const firstColumn = layoutStructure.children[0];
      if (!firstColumn.children) firstColumn.children = [];
      firstColumn.children.push({ type: "block", block: blockKey });
      setLayoutStructure({ ...layoutStructure });
    }
  };
  
  /**
   * Add a SuperAppObject block after it's been created/selected
   */
  const addSuperAppObjectBlock = (configObjName) => {
    const blockKey = `superappobject_${Date.now()}`;
    const newBlock = {
      type: "superappobject",
      configObj: configObjName,
      displayState: "TABLE",
    };
    
    const newBlocks = { ...blocks, [blockKey]: newBlock };
    setBlocks(newBlocks);
    
    // Add block to first available column
    if (layoutStructure && layoutStructure.children && layoutStructure.children.length > 0) {
      const firstColumn = layoutStructure.children[0];
      if (!firstColumn.children) firstColumn.children = [];
      firstColumn.children.push({ type: "block", block: blockKey });
      setLayoutStructure({ ...layoutStructure });
    }
    
    setShowSuperAppObjectEditor(false);
  };
  
  /**
   * Remove a block
   */
  const removeBlock = (blockKey) => {
    if (!window.confirm("Remove this block?")) return;
    
    const newBlocks = { ...blocks };
    delete newBlocks[blockKey];
    setBlocks(newBlocks);
    
    // Remove from structure
    const removeFromStructure = (node) => {
      if (Array.isArray(node)) {
        return node.filter(item => {
          if (item.type === "block" && item.block === blockKey) return false;
          if (item.children) {
            item.children = removeFromStructure(item.children);
          }
          return true;
        });
      }
      if (node.children) {
        node.children = removeFromStructure(node.children);
      }
      return node;
    };
    
    const newStructure = removeFromStructure(JSON.parse(JSON.stringify(layoutStructure)));
    setLayoutStructure(newStructure);
  };
  
  /**
   * Update layout when dropdown changes
   */
  const handleLayoutChange = (newLayout) => {
    setSelectedLayout(newLayout);
    const layoutOption = LAYOUT_OPTIONS.find(opt => opt.value === newLayout);
    if (layoutOption) {
      const newStructure = JSON.parse(JSON.stringify(layoutOption.structure));
      setLayoutStructure(newStructure);
    }
  };
  
  if (loading) {
    return (
      <div className="custom-container" style={{ maxWidth: 1400, margin: "32px auto", textAlign: "center", color: "#fff" }}>
        <p>Loading config...</p>
      </div>
    );
  }
  
  if (!cfg) {
    return (
      <div className="custom-container" style={{ maxWidth: 1400, margin: "32px auto", textAlign: "center", color: "#fff" }}>
        <p>No config found. Please create a config first.</p>
      </div>
    );
  }
  
  const availablePages = cfg.screens ? Object.keys(cfg.screens) : [];
  const currentScreen = cfg.screens?.[selectedPage];
  
  return (
    <div className="custom-container" style={{ maxWidth: 1400, margin: "32px auto", color: "#fff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <h2 style={{ margin: 0, color: "#fff" }}>Edit Mode - Page Editor</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <SuperAppButton
            className="primary-gradient-btn"
            size="sm"
            onClick={() => window.open(`/?appId=${encodeURIComponent(appId)}`, "_blank")}
          >
            View Site
          </SuperAppButton>
          <SuperAppButton
            className="primary-gradient-btn"
            size="sm"
            onClick={saveConfig}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Page"}
          </SuperAppButton>
        </div>
      </div>
      
      {/* Page Selector */}
      <div style={{ marginBottom: 24, padding: 16, background: "rgba(255,255,255,0.05)", borderRadius: 8 }}>
        <label style={{ display: "block", marginBottom: 8, color: "#fff" }}>
          <strong>Select Page to Edit:</strong>
        </label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {availablePages.length === 0 ? (
            <p style={{ color: "#fff", opacity: 0.7 }}>No pages found. Create pages in Menu Editor first.</p>
          ) : (
            availablePages.map((pageKey) => (
              <button
                key={pageKey}
                onClick={() => {
                  setSelectedPage(pageKey);
                  const screen = cfg.screens[pageKey];
                  if (screen?.layout) {
                    setBlocks(screen.layout.blocks || {});
                    setLayoutStructure(screen.layout.structure || null);
                    const cols = screen.layout.structure?.children || [];
                    if (cols.length === 1) setSelectedLayout("single");
                    else if (cols.length === 2) setSelectedLayout("two");
                    else if (cols.length === 3) setSelectedLayout("three");
                    else if (cols.length === 4) setSelectedLayout("four");
                    else setSelectedLayout("single");
                  }
                }}
                style={{
                  padding: "8px 16px",
                  background: selectedPage === pageKey ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.1)",
                  border: selectedPage === pageKey ? "2px solid #fff" : "1px solid rgba(255,255,255,0.2)",
                  borderRadius: 4,
                  color: "#fff",
                  cursor: "pointer",
                  fontWeight: selectedPage === pageKey ? "bold" : "normal",
                }}
              >
                {pageKey}
              </button>
            ))
          )}
        </div>
      </div>
      
      {selectedPage && (
        <>
          {/* Layout Selector */}
          <div style={{ marginBottom: 24, padding: 16, background: "rgba(255,255,255,0.05)", borderRadius: 8 }}>
            <label style={{ display: "block", marginBottom: 8, color: "#fff" }}>
              <strong>Layout:</strong>
            </label>
            <select
              value={selectedLayout}
              onChange={(e) => handleLayoutChange(e.target.value)}
              style={{
                width: "100%",
                maxWidth: 300,
                padding: 8,
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: 4,
                color: "#fff",
              }}
            >
              {LAYOUT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value} style={{ background: "#1d1769", color: "#fff" }}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Quadrant/Grid View for Adding Blocks */}
          <div style={{ marginBottom: 24, padding: 16, background: "rgba(255,255,255,0.05)", borderRadius: 8 }}>
            <h3 style={{ color: "#fff", marginTop: 0 }}>Add Blocks</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
              {BLOCK_TYPES.map((blockType) => (
                <button
                  key={blockType.type}
                  onClick={() => addBlock(blockType.type)}
                  style={{
                    padding: 16,
                    background: "rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: 8,
                    color: "#fff",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 8,
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.2)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                  }}
                >
                  <span style={{ fontSize: 32 }}>{blockType.icon}</span>
                  <span style={{ fontSize: 14, fontWeight: "bold" }}>{blockType.label}</span>
                </button>
              ))}
              <button
                onClick={() => setShowSuperAppObjectEditor(true)}
                style={{
                  padding: 16,
                  background: "rgba(255,255,255,0.1)",
                  border: "1px dashed rgba(255,255,255,0.3)",
                  borderRadius: 8,
                  color: "#fff",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.2)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                }}
              >
                <span style={{ fontSize: 32 }}>➕</span>
                <span style={{ fontSize: 14, fontWeight: "bold" }}>Create New SuperAppObject</span>
              </button>
            </div>
          </div>
          
          {/* Current Blocks Preview */}
          <div style={{ marginBottom: 24, padding: 16, background: "rgba(255,255,255,0.05)", borderRadius: 8 }}>
            <h3 style={{ color: "#fff", marginTop: 0 }}>Current Blocks ({Object.keys(blocks).length})</h3>
            {Object.keys(blocks).length === 0 ? (
              <p style={{ color: "#fff", opacity: 0.7, fontStyle: "italic" }}>No blocks yet. Add blocks above.</p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
                {Object.entries(blocks).map(([blockKey, block]) => (
                  <div
                    key={blockKey}
                    style={{
                      padding: 12,
                      background: "rgba(255,255,255,0.1)",
                      border: "1px solid rgba(255,255,255,0.2)",
                      borderRadius: 4,
                      position: "relative",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 8 }}>
                      <div>
                        <strong style={{ color: "#fff", fontSize: 12 }}>{block.type || "unknown"}</strong>
                        {block.title && (
                          <p style={{ color: "#fff", fontSize: 11, margin: "4px 0 0 0", opacity: 0.8 }}>
                            {block.title}
                          </p>
                        )}
                        {block.type === "superappobject" && block.configObj && (
                          <p style={{ color: "#fff", fontSize: 11, margin: "4px 0 0 0", opacity: 0.8 }}>
                            ConfigObj: {block.configObj}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => removeBlock(blockKey)}
                        style={{
                          padding: "4px 8px",
                          background: "rgba(255,0,0,0.2)",
                          border: "1px solid rgba(255,0,0,0.3)",
                          borderRadius: 4,
                          color: "#ff6b6b",
                          cursor: "pointer",
                          fontSize: 11,
                        }}
                      >
                        ✕
                      </button>
                    </div>
                    <pre style={{ fontSize: 10, color: "#fff", opacity: 0.6, margin: 0, overflow: "auto", maxHeight: 100 }}>
                      {JSON.stringify(block, null, 2).substring(0, 100)}...
                    </pre>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* SuperAppObject Editor Modal */}
          {showSuperAppObjectEditor && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0,0,0,0.8)",
                zIndex: 1000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 24,
              }}
            >
              <div
                style={{
                  background: "#1d1769",
                  borderRadius: 8,
                  padding: 24,
                  maxWidth: 1200,
                  maxHeight: "90vh",
                  overflow: "auto",
                  width: "100%",
                  position: "relative",
                }}
              >
                <button
                  onClick={() => setShowSuperAppObjectEditor(false)}
                  style={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    padding: "8px 12px",
                    background: "rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: 4,
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  ✕ Close
                </button>
                <h2 style={{ color: "#fff", marginTop: 0 }}>Create/Select SuperAppObject</h2>
                <UnifiedSuperAppObjectEditor
                  appId={appId}
                  onConfigObjCreated={(configObjName) => {
                    addSuperAppObjectBlock(configObjName);
                    loadConfigObjList();
                  }}
                />
                <div style={{ marginTop: 24, padding: 16, background: "rgba(255,255,255,0.05)", borderRadius: 8 }}>
                  <h3 style={{ color: "#fff", marginTop: 0 }}>Or Select Existing:</h3>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {configObjList.map((name) => (
                      <button
                        key={name}
                        onClick={() => addSuperAppObjectBlock(name)}
                        className="btn btn-primary"
                        style={{ fontSize: 12 }}
                      >
                        Use {name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

